// src/models/OrderTreatmentModal.js
// ✅ Complete Order & Treatment Modal for IPD
// - Order Tab: Doctors give Advice, Medicine, Notes to nurses (multi-doctor, timestamped)
// - Treatment Tab: Nurses record medicines given with doses, notes, timestamps
// - PDF generation for both Order Sheet and Treatment Sheet

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AppConst from '../apis/appconst';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const OrderTreatmentModal = ({
  show,
  onClose,
  patient,       // IPD patient row
  headerLogo,    // header image URL for PDF
  userType,      // 'doctor' | 'nurse' | 'admin'
  currentUser,   // { user_id, fname, lname, role_id }
  doctors = [],  // doctor list for display names
  initialTab = 'order', // 'order' | 'treatment' — which tab to open first
}) => {
  // ---- State ----
  const [activeMainTab, setActiveMainTab] = useState(initialTab); // 'order' | 'treatment'
  const [activeOrderTab, setActiveOrderTab] = useState('advice'); // 'advice' | 'medicine' | 'notes'

  // Order records (fetched from server)
  const [orders, setOrders] = useState([]); // array of order objects
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Treatment records
  const [treatments, setTreatments] = useState([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);

  // New order form
  const [orderForm, setOrderForm] = useState({ advice: '', medicine: '', notes: '' });

  // New treatment form
  const [treatmentForm, setTreatmentForm] = useState({
    medicine_name: '',
    dose: '',
    route: 'Oral',
    frequency: '',
    notes: ''
  });

  // Expanded rows for collapse
  const [expandedOrderIds, setExpandedOrderIds] = useState(new Set());
  const [expandedTreatmentIds, setExpandedTreatmentIds] = useState(new Set());
  
  // OT data for PDF (fetched from OT table)
  const [otData, setOtData] = useState(null);

  const ipdId = patient?.ipd_id || '';
  const ipdUhid = patient?.ipd_uhid || '';
  const userName = [currentUser?.fname, currentUser?.lname].filter(Boolean).join(' ') || 'Unknown';
  const userId = currentUser?.user_id || '';
  const isDoctor = userType === 'doctor' || String(currentUser?.role_id) === '3' || String(currentUser?.type) === 'doctor';
  const isNurse = userType === 'nurse' || String(currentUser?.role_id) === '4' || String(currentUser?.type) === 'nurse';
  const isAdminUser = String(currentUser?.role_id) === '1' || userType === 'admin';

  // Fetch OT details (OT Doctor, Anesthesia Doctor, Asst Nurse, Case etc.)
  const fetchOtData = useCallback(async () => {
    const uhid = patient?.ipd_uhid || patient?.ipd_bill_uhid || '';
    if (!uhid) return;
    try {
      const fd = new FormData();
      fd.append('ot_uhid_no', uhid);
      const res = await axios.post(
        `${AppConst.BASE_URL}${AppConst.getOtByUhidUrl || 'Webapis/get_ot_by_uhid'}`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res?.data?.status && res.data.ot_dt) {
        setOtData(res.data.ot_dt);
      }
    } catch (err) {
      console.warn('fetchOtData error:', err);
    }
  }, [patient]);

  // ---- Fetch Orders ----
  const fetchOrders = useCallback(async () => {
    if (!ipdId) return;
    setOrdersLoading(true);
    try {
      const fd = new FormData();
      fd.append('ipd_id', String(ipdId));
      fd.append('action', 'get_orders');
      const res = await axios.post(
        `${AppConst.BASE_URL}${AppConst.ipdOrderTreatmentUrl || 'Webapis/ipd_order_treatment'}`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data?.status && Array.isArray(res.data.records)) {
        setOrders(res.data.records);
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error('fetchOrders error:', err);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  }, [ipdId]);

  // ---- Fetch Treatments ----
  const fetchTreatments = useCallback(async () => {
    if (!ipdId) return;
    setTreatmentsLoading(true);
    try {
      const fd = new FormData();
      fd.append('ipd_id', String(ipdId));
      fd.append('action', 'get_treatments');
      const res = await axios.post(
        `${AppConst.BASE_URL}${AppConst.ipdOrderTreatmentUrl || 'Webapis/ipd_order_treatment'}`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data?.status && Array.isArray(res.data.records)) {
        setTreatments(res.data.records);
      } else {
        setTreatments([]);
      }
    } catch (err) {
      console.error('fetchTreatments error:', err);
      setTreatments([]);
    } finally {
      setTreatmentsLoading(false);
    }
  }, [ipdId]);

  useEffect(() => {
    if (show && ipdId) {
      fetchOrders();
      fetchTreatments();
      fetchOtData();
      setActiveMainTab(initialTab);
    }
  }, [show, ipdId, fetchOrders, fetchTreatments, fetchOtData, initialTab]);

  // ---- Save Order ----
  const saveOrder = async () => {
    const content = orderForm[activeOrderTab]?.trim();
    if (!content) {
      toast.error(`Please enter ${activeOrderTab} content.`);
      return;
    }
    try {
      const fd = new FormData();
      fd.append('action', 'add_order');
      fd.append('ipd_id', String(ipdId));
      fd.append('ipd_uhid', ipdUhid);
      fd.append('order_type', activeOrderTab); // advice | medicine | notes
      fd.append('order_content', content);
      fd.append('doctor_id', userId);
      fd.append('doctor_name', userName);

      const res = await axios.post(
        `${AppConst.BASE_URL}${AppConst.ipdOrderTreatmentUrl || 'Webapis/ipd_order_treatment'}`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (res.data?.status) {
        toast.success(`${activeOrderTab.charAt(0).toUpperCase() + activeOrderTab.slice(1)} saved successfully`);
        setOrderForm(prev => ({ ...prev, [activeOrderTab]: '' }));
        fetchOrders();
      } else {
        toast.error(res.data?.message || 'Failed to save order');
      }
    } catch (err) {
      console.error('saveOrder error:', err);
      toast.error('Server error saving order');
    }
  };

  // ---- Save Treatment ----
  const saveTreatment = async () => {
    if (!treatmentForm.medicine_name.trim()) {
      toast.error('Please enter medicine name.');
      return;
    }
    if (!treatmentForm.dose.trim()) {
      toast.error('Please enter dose.');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('action', 'add_treatment');
      fd.append('ipd_id', String(ipdId));
      fd.append('ipd_uhid', ipdUhid);
      fd.append('medicine_name', treatmentForm.medicine_name);
      fd.append('dose', treatmentForm.dose);
      fd.append('route', treatmentForm.route);
      fd.append('frequency', treatmentForm.frequency);
      fd.append('notes', treatmentForm.notes);
      fd.append('nurse_id', userId);
      fd.append('nurse_name', userName);

      const res = await axios.post(
        `${AppConst.BASE_URL}${AppConst.ipdOrderTreatmentUrl || 'Webapis/ipd_order_treatment'}`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (res.data?.status) {
        toast.success('Treatment saved successfully');
        setTreatmentForm({ medicine_name: '', dose: '', route: 'Oral', frequency: '', notes: '' });
        fetchTreatments();
      } else {
        toast.error(res.data?.message || 'Failed to save treatment');
      }
    } catch (err) {
      console.error('saveTreatment error:', err);
      toast.error('Server error saving treatment');
    }
  };

  // ---- Delete Order ----
  const deleteOrder = async (orderId) => {
    const r = await Swal.fire({ 
      title: 'Delete this order?', 
      icon: 'warning', 
      showCancelButton: true, 
      confirmButtonText: 'Yes, delete', 
      confirmButtonColor: '#d33',
      customClass: { container: 'swal-above-modal' }
    });
    if (!r.isConfirmed) return;
    try {
      const fd = new FormData();
      fd.append('action', 'delete_order');
      fd.append('order_id', String(orderId));
      const res = await axios.post(`${AppConst.BASE_URL}${AppConst.ipdOrderTreatmentUrl || 'Webapis/ipd_order_treatment'}`, fd);
      if (res.data?.status) { toast.success('Order deleted'); fetchOrders(); }
      else toast.error('Failed to delete');
    } catch (err) { toast.error('Server error'); }
  };

  // ---- Delete Treatment ----
  const deleteTreatment = async (treatmentId) => {
    const r = await Swal.fire({ 
      title: 'Delete this treatment?', 
      icon: 'warning', 
      showCancelButton: true, 
      confirmButtonText: 'Yes, delete', 
      confirmButtonColor: '#d33',
      customClass: { container: 'swal-above-modal' }
    });
    if (!r.isConfirmed) return;
    try {
      const fd = new FormData();
      fd.append('action', 'delete_treatment');
      fd.append('treatment_id', String(treatmentId));
      const res = await axios.post(`${AppConst.BASE_URL}${AppConst.ipdOrderTreatmentUrl || 'Webapis/ipd_order_treatment'}`, fd);
      if (res.data?.status) { toast.success('Treatment deleted'); fetchTreatments(); }
      else toast.error('Failed to delete');
    } catch (err) { toast.error('Server error'); }
  };

  // ---- Toggle Expand ----
  const toggleExpand = (id, type) => {
    const setter = type === 'order' ? setExpandedOrderIds : setExpandedTreatmentIds;
    setter(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- Image to Base64 for PDF ----
  const imageUrlToBase64 = async (url) => {
    if (!url) return null;
    try {
      const response = await fetch(url, { cache: 'no-cache' });
      if (!response.ok) return null;
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) { return null; }
  };

  // ---- Format DateTime ----
  const fmtDt = (dtStr) => {
    if (!dtStr) return '';
    try {
      const d = new Date(dtStr);
      if (isNaN(d.getTime())) return dtStr;
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch (e) { return dtStr; }
  };

  // ---- Print Order Sheet PDF ----
  const printOrderSheet = async () => {
    if (orders.length === 0) { toast.info('No orders to print'); return; }

    const doc = new jsPDF('p', 'pt', 'a4');
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const margin = 40;
    let y = 0;

    // Header
    try {
      const hBase64 = await imageUrlToBase64(headerLogo);
      if (hBase64) { doc.addImage(hBase64, 'PNG', 0, 0, pw, 80); y = 85; }
      else y = 20;
    } catch (e) { y = 20; }

    // Title
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.setDrawColor(0); doc.rect(margin, y, pw - margin * 2, 22);
    doc.text('DOCTOR\'S ORDER SHEET', pw / 2, y + 15, { align: 'center' });
    y += 32;

    // Patient Details
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    const lx = margin + 5, rx = pw / 2 + 40, lw = 95;
    const pRow = (label, val, x, yy) => {
      doc.setFont('helvetica', 'bold'); doc.text(label, x, yy);
      doc.setFont('helvetica', 'normal'); doc.text(`: ${val || 'N/A'}`, x + lw, yy);
    };

    pRow('UHID', patient?.ipd_uhid, lx, y);
    pRow('IPD No', `000${patient?.ipd_id || ''}`, rx, y); y += 14;
    pRow('Patient Name', `${patient?.ipd_patient_title || ''} ${patient?.ipd_patient_name || ''}`.trim(), lx, y);
    pRow('Age / Gender', `${patient?.ipd_age || patient?.ipd_patient_age || ''} / ${patient?.ipd_patient_gender || ''}`, rx, y); y += 14;
    pRow('Ward / Bed', `${patient?.ipd_ward_name || ''} / ${patient?.ipd_bed_name || ''}`, lx, y);
    pRow('Admission Date', fmtDt(patient?.ipd_reg_date), rx, y); y += 14;
    pRow('Referred Doctor', patient?.ipd_refer_doctor_name || '', lx, y);
    pRow('OT Doctor', otData?.ot_doctor_name || patient?.ot_doctor_name || patient?.ot_doctor || '', rx, y); y += 14;
    pRow('Anesthesia Doctor', otData?.ot_anesthesia_doctor_name || patient?.ot_anesthesia_doctor_name || patient?.ot_anesthesia_doctor || '', lx, y);
    pRow('Asst. Nurse', otData?.ot_asst_nurse_name || patient?.ot_asst_doctor_name || '', rx, y); y += 14;
    pRow('Case', otData?.ot_case_name || patient?.ipd_case || '', lx, y);
    pRow('OT Date', fmtDt(otData?.ot_datetime || otData?.ot_reg_date || ''), rx, y);
    y += 10;

    doc.line(margin, y, pw - margin, y); y += 10;

    // Group orders by type
    const types = ['advice', 'medicine', 'notes'];
    for (const type of types) {
      const filtered = orders.filter(o => o.order_type === type);
      if (filtered.length === 0) continue;

      doc.setFontSize(11); doc.setFont('helvetica', 'bold');
      doc.setFillColor(240, 245, 251);
      doc.rect(margin, y - 4, pw - margin * 2, 16, 'F');
      doc.text(type.charAt(0).toUpperCase() + type.slice(1), margin + 5, y + 8);
      y += 18;

      const rows = filtered.map((o, i) => [
        i + 1,
        o.order_content || '',
        o.doctor_name || '',
        fmtDt(o.created_at || o.order_datetime)
      ]);

      autoTable(doc, {
        startY: y,
        head: [['#', 'Content', 'Doctor', 'Date & Time']],
        body: rows,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [64, 81, 137], textColor: 255, fontStyle: 'bold' },
        columnStyles: { 0: { cellWidth: 30, halign: 'center' }, 1: { cellWidth: pw - margin * 2 - 200 }, 2: { cellWidth: 90 }, 3: { cellWidth: 80 } },
        margin: { left: margin, right: margin },
        didDrawPage: () => {
          // Add footer on each page
        }
      });

      y = doc.lastAutoTable.finalY + 15;

      if (y > ph - 100) { doc.addPage(); y = 30; }
    }

    // Signatures
    y = Math.max(y + 30, ph - 80);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.line(margin, y, margin + 120, y);
    doc.text('Doctor Signature', margin, y + 12);
    doc.line(pw - margin - 120, y, pw - margin, y);
    doc.text('Nurse Signature', pw - margin - 120, y + 12);

    window.open(doc.output('bloburl'), '_blank');
  };

  // ---- Print Treatment Sheet PDF ----
  const printTreatmentSheet = async () => {
    if (treatments.length === 0) { toast.info('No treatments to print'); return; }

    const doc = new jsPDF('p', 'pt', 'a4');
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const margin = 40;
    let y = 0;

    // Header
    try {
      const hBase64 = await imageUrlToBase64(headerLogo);
      if (hBase64) { doc.addImage(hBase64, 'PNG', 0, 0, pw, 80); y = 85; }
      else y = 20;
    } catch (e) { y = 20; }

    // Title
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.setDrawColor(0); doc.rect(margin, y, pw - margin * 2, 22);
    doc.text('TREATMENT SHEET', pw / 2, y + 15, { align: 'center' });
    y += 32;

    // Patient Details (same as order sheet)
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    const lx = margin + 5, rx = pw / 2 + 40, lw = 95;
    const pRow = (label, val, x, yy) => {
      doc.setFont('helvetica', 'bold'); doc.text(label, x, yy);
      doc.setFont('helvetica', 'normal'); doc.text(`: ${val || 'N/A'}`, x + lw, yy);
    };

    pRow('UHID', patient?.ipd_uhid, lx, y);
    pRow('IPD No', `000${patient?.ipd_id || ''}`, rx, y); y += 14;
    pRow('Patient Name', `${patient?.ipd_patient_title || ''} ${patient?.ipd_patient_name || ''}`.trim(), lx, y);
    pRow('Age / Gender', `${patient?.ipd_age || patient?.ipd_patient_age || ''} / ${patient?.ipd_patient_gender || ''}`, rx, y); y += 14;
    pRow('Ward / Bed', `${patient?.ipd_ward_name || ''} / ${patient?.ipd_bed_name || ''}`, lx, y);
    pRow('Admission Date', fmtDt(patient?.ipd_reg_date), rx, y); y += 10;

    doc.line(margin, y, pw - margin, y); y += 10;

    // Treatment table
    const rows = treatments.map((t, i) => [
      i + 1,
      t.medicine_name || '',
      t.dose || '',
      t.route || '',
      t.frequency || '',
      t.notes || '',
      t.nurse_name || '',
      fmtDt(t.created_at || t.treatment_datetime)
    ]);

    autoTable(doc, {
      startY: y,
      head: [['#', 'Medicine', 'Dose', 'Route', 'Frequency', 'Notes', 'Nurse', 'Date & Time']],
      body: rows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [64, 81, 137], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 25, halign: 'center' },
        1: { cellWidth: 80 },
        2: { cellWidth: 50 },
        3: { cellWidth: 40 },
        4: { cellWidth: 55 },
        5: { cellWidth: 70 },
        6: { cellWidth: 65 },
        7: { cellWidth: pw - margin * 2 - 385 }
      },
      margin: { left: margin, right: margin }
    });

    y = doc.lastAutoTable.finalY + 40;
    if (y > ph - 60) y = ph - 60;

    // Signatures
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.line(margin, y, margin + 120, y);
    doc.text('Nurse Signature', margin, y + 12);
    doc.line(pw - margin - 120, y, pw - margin, y);
    doc.text('Doctor Signature', pw - margin - 120, y + 12);

    window.open(doc.output('bloburl'), '_blank');
  };

  if (!show) return null;

  // ---- Group orders by type for display ----
  const adviceOrders = orders.filter(o => o.order_type === 'advice');
  const medicineOrders = orders.filter(o => o.order_type === 'medicine');
  const notesOrders = orders.filter(o => o.order_type === 'notes');
  const currentOrderList = activeOrderTab === 'advice' ? adviceOrders : activeOrderTab === 'medicine' ? medicineOrders : notesOrders;

  return (
    <>
      {/* ✅ CSS to ensure SweetAlert2 appears ABOVE this modal */}
      <style>{`
        .swal-above-modal { z-index: 99999 !important; }
      `}</style>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, width: '95%', maxWidth: 900, maxHeight: '92vh', overflow: 'hidden', boxShadow: '0 12px 48px rgba(0,0,0,0.25)' }}
        onClick={e => e.stopPropagation()}>

        {/* ---- HEADER ---- */}
        <div style={{ background: 'linear-gradient(135deg, #405189, #6366f1)', color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h5 style={{ margin: 0, fontWeight: 700 }}>Order & Treatment</h5>
            <small style={{ opacity: 0.9 }}>
              {patient?.ipd_patient_name || ''} | UHID: {ipdUhid} | IPD: 000{ipdId}
            </small>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>

        {/* ---- MAIN TABS ---- */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb' }}>
          {['order', 'treatment'].map(tab => (
            <button key={tab}
              onClick={() => setActiveMainTab(tab)}
              style={{
                flex: 1, padding: '12px 0', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
                background: activeMainTab === tab ? '#fff' : '#f3f4f6',
                color: activeMainTab === tab ? '#405189' : '#6b7280',
                borderBottom: activeMainTab === tab ? '3px solid #405189' : '3px solid transparent'
              }}>
              {tab === 'order' ? '📋 Doctor\'s Orders' : '💊 Treatment (Nurse)'}
            </button>
          ))}
        </div>

        {/* ---- BODY ---- */}
        <div style={{ padding: 20, maxHeight: 'calc(92vh - 140px)', overflowY: 'auto' }}>

          {/* ============ ORDER TAB ============ */}
          {activeMainTab === 'order' && (
            <>
              {/* Sub-tabs: Advice | Medicine | Notes */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['advice', 'medicine', 'notes'].map(tab => (
                  <button key={tab}
                    onClick={() => setActiveOrderTab(tab)}
                    style={{
                      padding: '8px 20px', borderRadius: 20, border: activeOrderTab === tab ? '2px solid #405189' : '1px solid #d1d5db',
                      background: activeOrderTab === tab ? '#eef2ff' : '#fff',
                      color: activeOrderTab === tab ? '#405189' : '#374151',
                      fontWeight: activeOrderTab === tab ? 600 : 400, cursor: 'pointer', fontSize: 13
                    }}>
                    {tab === 'advice' ? '📝 Advice' : tab === 'medicine' ? '💊 Medicine' : '📋 Notes'}
                    <span style={{ marginLeft: 6, background: '#e5e7eb', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>
                      {tab === 'advice' ? adviceOrders.length : tab === 'medicine' ? medicineOrders.length : notesOrders.length}
                    </span>
                  </button>
                ))}
              </div>

              {/* Add new order form (doctors & admins only) */}
              {(isDoctor || isAdminUser) && (
                <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid #e5e7eb' }}>
                  <label style={{ fontWeight: 600, marginBottom: 6, display: 'block', color: '#374151' }}>
                    Add {activeOrderTab.charAt(0).toUpperCase() + activeOrderTab.slice(1)}
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder={`Enter ${activeOrderTab}...`}
                    value={orderForm[activeOrderTab]}
                    onChange={e => setOrderForm(prev => ({ ...prev, [activeOrderTab]: e.target.value }))}
                    style={{ marginBottom: 10 }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <small style={{ color: '#6b7280' }}>By: <strong>{userName}</strong> | {new Date().toLocaleString('en-IN')}</small>
                    <button className="btn btn-primary btn-sm" onClick={saveOrder} style={{ padding: '6px 24px' }}>
                      <i className="ri-save-line me-1"></i>Save
                    </button>
                  </div>
                </div>
              )}

              {/* Order list */}
              {ordersLoading ? (
                <div className="text-center text-muted py-4">Loading orders...</div>
              ) : currentOrderList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, background: '#f9fafb', borderRadius: 8 }}>
                  <div style={{ fontSize: 40, color: '#d1d5db' }}>📋</div>
                  <p style={{ color: '#6b7280', marginTop: 8 }}>No {activeOrderTab} orders yet</p>
                </div>
              ) : (
                <div>
                  {currentOrderList.map((order, idx) => {
                    const isExpanded = expandedOrderIds.has(order.order_id || idx);
                    return (
                      <div key={order.order_id || idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                        {/* Collapsed header */}
                        <div
                          onClick={() => toggleExpand(order.order_id || idx, 'order')}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: isExpanded ? '#eef2ff' : '#fff' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontWeight: 600, color: '#405189', width: 24 }}>{idx + 1}.</span>
                            <div>
                              <div style={{ fontWeight: 500, color: '#111827', fontSize: 13 }}>
                                {String(order.order_content || '').substring(0, 80)}{String(order.order_content || '').length > 80 ? '...' : ''}
                              </div>
                              <small style={{ color: '#6b7280' }}>
                                Dr. {order.doctor_name || 'Unknown'} — {fmtDt(order.created_at || order.order_datetime)}
                              </small>
                            </div>
                          </div>
                          <span style={{ fontSize: 18, color: '#9ca3af' }}>{isExpanded ? '▲' : '▼'}</span>
                        </div>

                        {/* Expanded content */}
                        {isExpanded && (
                          <div style={{ padding: '12px 14px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: '#374151', marginBottom: 10 }}>
                              {order.order_content}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <small style={{ color: '#6b7280' }}>
                                <strong>Doctor:</strong> {order.doctor_name} &nbsp;|&nbsp;
                                <strong>ID:</strong> {order.doctor_id} &nbsp;|&nbsp;
                                <strong>Time:</strong> {fmtDt(order.created_at || order.order_datetime)}
                              </small>
                              {(isAdminUser || String(order.doctor_id) === String(userId)) && (
                                <button className="btn btn-sm btn-outline-danger" onClick={() => deleteOrder(order.order_id)}>
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Print Order Sheet */}
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button className="btn btn-outline-primary btn-sm" onClick={printOrderSheet}>
                  <i className="ri-printer-line me-1"></i>Print Order Sheet
                </button>
              </div>
            </>
          )}

          {/* ============ TREATMENT TAB ============ */}
          {activeMainTab === 'treatment' && (
            <>
              {/* Patient details bar */}
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13 }}>
                  <div><strong>Patient:</strong> {patient?.ipd_patient_name || ''}</div>
                  <div><strong>UHID:</strong> {ipdUhid}</div>
                  <div><strong>IPD:</strong> 000{ipdId}</div>
                  <div><strong>Ward/Bed:</strong> {patient?.ipd_ward_name || ''} / {patient?.ipd_bed_name || ''}</div>
                </div>
              </div>

              {/* Add treatment form (nurses & admins) */}
              {(isNurse || isAdminUser || isDoctor) && (
                <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid #e5e7eb' }}>
                  <h6 style={{ fontWeight: 600, marginBottom: 12, color: '#374151' }}>Add Medicine / Treatment</h6>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ fontWeight: 500, fontSize: 12, display: 'block', marginBottom: 4 }}>Medicine Name *</label>
                      <input className="form-control form-control-sm" placeholder="Medicine name"
                        value={treatmentForm.medicine_name} onChange={e => setTreatmentForm(p => ({ ...p, medicine_name: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 500, fontSize: 12, display: 'block', marginBottom: 4 }}>Dose *</label>
                      <input className="form-control form-control-sm" placeholder="e.g. 500mg, 10ml"
                        value={treatmentForm.dose} onChange={e => setTreatmentForm(p => ({ ...p, dose: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 500, fontSize: 12, display: 'block', marginBottom: 4 }}>Route</label>
                      <select className="form-control form-control-sm" value={treatmentForm.route}
                        onChange={e => setTreatmentForm(p => ({ ...p, route: e.target.value }))}>
                        {['Oral', 'IV', 'IM', 'SC', 'Topical', 'Inhale', 'Rectal', 'SL', 'Other'].map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontWeight: 500, fontSize: 12, display: 'block', marginBottom: 4 }}>Frequency</label>
                      <input className="form-control form-control-sm" placeholder="e.g. OD, BD, TDS, QID, SOS"
                        value={treatmentForm.frequency} onChange={e => setTreatmentForm(p => ({ ...p, frequency: e.target.value }))} />
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={{ fontWeight: 500, fontSize: 12, display: 'block', marginBottom: 4 }}>Notes</label>
                      <input className="form-control form-control-sm" placeholder="Additional notes"
                        value={treatmentForm.notes} onChange={e => setTreatmentForm(p => ({ ...p, notes: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                    <small style={{ color: '#6b7280' }}>By: <strong>{userName}</strong> | {new Date().toLocaleString('en-IN')}</small>
                    <button className="btn btn-success btn-sm" onClick={saveTreatment} style={{ padding: '6px 24px' }}>
                      <i className="ri-save-line me-1"></i>Save Treatment
                    </button>
                  </div>
                </div>
              )}

              {/* Treatment list */}
              {treatmentsLoading ? (
                <div className="text-center text-muted py-4">Loading treatments...</div>
              ) : treatments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, background: '#f9fafb', borderRadius: 8 }}>
                  <div style={{ fontSize: 40, color: '#d1d5db' }}>💊</div>
                  <p style={{ color: '#6b7280', marginTop: 8 }}>No treatments recorded yet</p>
                </div>
              ) : (
                <div>
                  {treatments.map((t, idx) => {
                    const isExpanded = expandedTreatmentIds.has(t.treatment_id || idx);
                    return (
                      <div key={t.treatment_id || idx} style={{ border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                        <div onClick={() => toggleExpand(t.treatment_id || idx, 'treatment')}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: isExpanded ? '#f0fdf4' : '#fff' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontWeight: 600, color: '#059669', width: 24 }}>{idx + 1}.</span>
                            <div>
                              <div style={{ fontWeight: 500, fontSize: 13 }}>
                                <strong>{t.medicine_name}</strong> — {t.dose} ({t.route})
                                {t.frequency && <span style={{ color: '#6b7280' }}> | {t.frequency}</span>}
                              </div>
                              <small style={{ color: '#6b7280' }}>
                                Nurse: {t.nurse_name || 'Unknown'} — {fmtDt(t.created_at || t.treatment_datetime)}
                              </small>
                            </div>
                          </div>
                          <span style={{ fontSize: 18, color: '#9ca3af' }}>{isExpanded ? '▲' : '▼'}</span>
                        </div>

                        {isExpanded && (
                          <div style={{ padding: '12px 14px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 13 }}>
                              <div><strong>Medicine:</strong> {t.medicine_name}</div>
                              <div><strong>Dose:</strong> {t.dose}</div>
                              <div><strong>Route:</strong> {t.route}</div>
                              <div><strong>Frequency:</strong> {t.frequency || '-'}</div>
                              <div><strong>Notes:</strong> {t.notes || '-'}</div>
                              <div><strong>Time:</strong> {fmtDt(t.created_at || t.treatment_datetime)}</div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                              {(isAdminUser || String(t.nurse_id) === String(userId)) && (
                                <button className="btn btn-sm btn-outline-danger" onClick={() => deleteTreatment(t.treatment_id)}>
                                  <i className="ri-delete-bin-line me-1"></i>Delete
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Print Treatment Sheet */}
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <button className="btn btn-outline-success btn-sm" onClick={printTreatmentSheet}>
                  <i className="ri-printer-line me-1"></i>Print Treatment Sheet
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default OrderTreatmentModal;